// Every recipe gets something to look at.
//
// A recipe can arrive without a photo from any direction: typed into the New
// Recipe form, written by the chatbot, created over MCP, or restored from a
// revision that predates the field. Rather than leave a grey hole on the page
// — or block the cook until they go and find a URL — each write path fills a
// themed food photo, and only when nothing is set. A real photo the cook
// chose is never touched.
//
// loremflickr is a free, key-less Flickr proxy: the tags steer it at roughly
// the right subject and `lock` pins it to one photo, so the same recipe keeps
// the same picture instead of reshuffling on every render.
//
// Deliberately mirrored, byte for byte, in mcp/main.py (_recipe_image_url) and
// chat/main.py (_placeholder_image_url) — all three write the same recipes, so
// a recipe must not change its photo depending on which one filled it in.

export type ImageSubject = {
  id?: string;
  title?: string;
  category?: string;
  imageUrl?: string;
};

// djb2, kept to 32 bits. Any stable hash would do; what matters is that the
// three implementations agree, which rules out language built-ins — Python's
// hash() is salted per process, so the same recipe drew a different photo
// after every restart.
function stableHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h * 33 + s.charCodeAt(i)) % 4294967296;
  }
  return h;
}

/** A themed food photo URL for a recipe, stable for a given recipe id. */
export function recipeImageUrl(recipe: ImageSubject): string {
  const title = (recipe.title ?? "").trim();
  const category = (recipe.category ?? "").trim().toLowerCase();
  const key = (recipe.id ?? title ?? "x").trim();

  const tags = ["food"];
  if (category && !tags.includes(category)) tags.push(category);
  // First word of the title, alphanumerics only — loremflickr takes bare tags.
  const firstWord = (title.split(/\s+/)[0] ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (firstWord && !tags.includes(firstWord)) tags.push(firstWord);

  const lock = stableHash(key) % 100000;
  return `https://loremflickr.com/800/450/${tags.join(",")}?lock=${lock}`;
}

/** True when the recipe has no usable photo of its own. */
export function needsImage(recipe: ImageSubject): boolean {
  return !recipe.imageUrl || !recipe.imageUrl.trim();
}

/**
 * Fill in a photo if there isn't one. Returns a new object — callers are
 * building Firestore payloads and shouldn't have their input mutated.
 * `idFallback` names the recipe when the object itself carries no id yet
 * (a copy being written under a fresh slug, say).
 */
export function withImage<T extends ImageSubject>(recipe: T, idFallback?: string): T {
  if (!needsImage(recipe)) return recipe;
  return {
    ...recipe,
    imageUrl: recipeImageUrl({ ...recipe, id: recipe.id ?? idFallback }),
  };
}
